"use client";

/**
 * Client body for the approved-replies use-case page. Lives apart from
 * `page.tsx` (which stays a server component for `metadata`) so it can call
 * `useT()` and follow the visitor's locale, exactly like the homepage. All
 * copy comes from the `ucApprovedReplies` namespace; structure (icons, hrefs,
 * capability status) stays here.
 */

import {
  PencilLine,
  Bot,
  Send,
  ListChecks,
  ShieldCheck,
  Lock,
  RefreshCw,
  Activity,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  ProductShot,
  Section,
  SectionHeader,
  WorkflowSteps,
  FeatureGrid,
  CapabilityGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useT } from "@/i18n";

export function ApprovedRepliesContent() {
  const t = useT();
  const k = (s: string) => `ucApprovedReplies.${s}`;

  const steps = [
    { key: "propose", icon: <Bot size={18} /> },
    { key: "edit", icon: <PencilLine size={18} /> },
    { key: "target", icon: <ListChecks size={18} /> },
    { key: "approve", icon: <Send size={18} /> },
  ].map((s) => ({
    icon: s.icon,
    tag: t(k(`steps.${s.key}.tag`)),
    title: t(k(`steps.${s.key}.title`)),
    body: t(k(`steps.${s.key}.body`)),
  }));

  const features = [
    { key: "draft", icon: <PencilLine size={18} /> },
    { key: "target", icon: <ListChecks size={18} /> },
    { key: "ledger", icon: <ShieldCheck size={18} /> },
    { key: "outbox", icon: <RefreshCw size={18} /> },
    { key: "health", icon: <Activity size={18} /> },
    { key: "noSilent", icon: <Lock size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const capabilities = [
    { key: "freshdeskReply", status: "supported" as const },
    { key: "freshdeskNote", status: "supported" as const },
    { key: "jiraComment", status: "supported" as const },
    { key: "jsmReply", status: "conditional" as const },
    { key: "chatThread", status: "supported" as const },
    { key: "autoSend", status: "unavailable" as const },
  ].map((c) => ({
    name: t(k(`capabilities.${c.key}.name`)),
    status: c.status,
    body: t(k(`capabilities.${c.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5", "q6"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/demo", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <ProductShot src="/screenshots/issues.png" alt={t(k("shot.alt"))} label={t(k("shot.label"))} />

      <Section>
        <SectionHeader
          eyebrow={t(k("how.eyebrow"))}
          title={t(k("how.title"))}
          description={t(k("how.description"))}
        />
        <WorkflowSteps steps={steps} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("features.eyebrow"))}
          title={t(k("features.title"))}
          description={t(k("features.description"))}
        />
        <FeatureGrid features={features} />
      </Section>

      <Section>
        <SectionHeader
          eyebrow={t(k("targets.eyebrow"))}
          title={t(k("targets.title"))}
          description={t(k("targets.description"))}
        />
        <CapabilityGrid
          capabilities={capabilities}
          statusLabels={{
            supported: t("mk.cap.supported"),
            conditional: t("mk.cap.conditional"),
            unavailable: t("mk.cap.unavailable"),
          }}
        />
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("governed.eyebrow"))} title={t(k("governed.title"))} />
        <SecurityNote icon={<Lock size={18} />} title={t(k("governed.noteTitle"))}>
          {t(k("governed.noteBody"))}
        </SecurityNote>
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section muted>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/use-cases/ai-agent-approval-ledger",
              label: t(k("related.ledger.label")),
              description: t(k("related.ledger.description")),
            },
            {
              href: "/integrations/freshdesk",
              label: t(k("related.freshdesk.label")),
              description: t(k("related.freshdesk.description")),
            },
            {
              href: "/integrations/jira",
              label: t(k("related.jira.label")),
              description: t(k("related.jira.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
